export type HapticType = 'light' | 'medium' | 'heavy' | 'selection' | 'success' | 'warning' | 'error';
export type StatusBarStyle = 'light' | 'dark' | 'default';

// Gemeinsame Pfad-Sicherheitspruefung mit deepLinks.ts: ein serverseitig
// eingebetteter `additionalData.route` (OneSignal) muss die gleichen Regeln
// erfüllen wie ein Universal-Link — kein Protokoll, kein `..`, kein Backslash.
import { isSafeAppPath } from './deepLinks';
import { reportPushOpened } from './pushOpened';
import { ENV_FORCE_PREF_KEY, ENV_PREF_KEY } from '../env/appEnv';

export interface DeviceInfo {
  id?: string;
  identifier?: string;
  platform?: string;
  operatingSystem?: string;
  osVersion?: string;
  model?: string;
  manufacturer?: string;
  isVirtual?: boolean;
  webViewVersion?: string;
  [key: string]: unknown;
}

export interface NativeNetworkStatus {
  connected?: boolean;
  connectionType?: string;
}

export interface BackCloserToken {
  id: number;
  name: string;
}

type NativeHandler = () => void;
type BackCloser = () => boolean | void;
type StreamGuard = () => boolean;
type BridgeFn = (...args: unknown[]) => unknown;
type BridgeObject = Record<string, unknown>;
interface NativeBackEvent {
  canGoBack?: boolean;
  preventDefault?: () => void;
}

interface NativeSubscription {
  remove?: () => Promise<void> | void;
}
type PluginLoader<T> = () => Promise<T>;

interface LegacyNativeBridge extends BridgeObject {
  WTN?: LegacyNativeBridge;
}

interface NativeWindow extends Window {
  WTN?: LegacyNativeBridge;
  NativeBridge?: LegacyNativeBridge;
  Android?: LegacyNativeBridge;
  LorathBackHandler?: BackHandlerBridge;
  customBackHandling?: (event?: NativeBackEvent) => boolean;
  lorathChatStreamActive?: unknown;
  lorathTableRoundStreamActive?: unknown;
  t?: (key: string) => string;
}

interface BackHandlerBridge {
  register: (handler: BackCloser) => () => void;
  push: (handler: BackCloser) => () => void;
  add: (handler: BackCloser) => void;
  remove: (handler: BackCloser) => void;
  registerCloser: (name: string | BackCloser, closeFn?: BackCloser) => BackCloserToken | null;
  unregisterCloser: (token: BackCloserToken | BackCloser | null | undefined) => boolean;
  isEmpty: () => boolean;
  setStreamGuard: (fn: StreamGuard) => BackCloserToken | null;
}

const backClosers: { token: BackCloserToken; closeFn: BackCloser; name: string }[] = [];
const streamGuards: { token: BackCloserToken; guardFn: StreamGuard }[] = [];
let nextBackTokenId = 1;
let nativeBackInstallPromise: Promise<void> | null = null;
let legacyBackRegistered = false;

function getWindow(): NativeWindow | null {
  return typeof window === 'undefined' ? null : window as NativeWindow;
}

async function getCapacitor(): Promise<typeof import('@capacitor/core').Capacitor | null> {
  try {
    const { Capacitor } = await import('@capacitor/core');
    return Capacitor;
  } catch {
    return null;
  }
}

export async function isNativeCapacitorPlatform(): Promise<boolean> {
  const Capacitor = await getCapacitor();
  return Capacitor?.isNativePlatform() === true;
}

/**
 * Synchroner, Capacitor-EXKLUSIVER Native-Check (kein User-Agent-Rateschluss).
 * `platform.os` faellt im mobilen Web (PWA auf app.lorath.eu, KEIN Capacitor) per
 * UA auf 'ios'/'android' zurueck — das darf NICHT als nativ gelten, sonst laufen
 * z.B. Rechtstext-Links ins Leere (openExternal ist ohne Capacitor ein No-Op).
 * `window.Capacitor` wird ausschliesslich in der echten App injiziert.
 */
export function isCapacitorNativeSync(): boolean {
  if (typeof window === 'undefined') return false;
  const cap = (window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return cap?.isNativePlatform?.() === true;
}

async function loadCapacitorPlugin<T>(pluginName: string, loader: PluginLoader<T>): Promise<T | null> {
  try {
    const pluginModule = await loader();
    const Capacitor = await getCapacitor();
    return Capacitor?.isPluginAvailable(pluginName) === true ? pluginModule : null;
  } catch {
    return null;
  }
}

function getLegacyBridge(): LegacyNativeBridge | null {
  const win = getWindow();
  return win?.WTN ?? win?.NativeBridge ?? win?.Android ?? null;
}

function getLegacyMethod(...paths: string[]): BridgeFn | null {
  const bridge = getLegacyBridge();
  if (!bridge) return null;

  for (const path of paths) {
    const parts = path.split('.');
    let context: BridgeObject = bridge;
    let value: unknown = bridge;

    for (const part of parts) {
      if (!value || typeof value !== 'object') {
        value = null;
        break;
      }
      context = value as BridgeObject;
      value = context[part];
    }

    if (typeof value === 'function') return value.bind(context) as BridgeFn;
  }

  return null;
}

async function invoke(method: BridgeFn | null, ...args: unknown[]): Promise<unknown> {
  if (!method) return undefined;
  try {
    return await method(...args);
  } catch {
    return undefined;
  }
}

function asDeviceInfo(value: unknown): DeviceInfo | null {
  return value && typeof value === 'object' ? value as DeviceInfo : null;
}

function createToken(name: string): BackCloserToken {
  return { id: nextBackTokenId++, name };
}

export function registerCloser(name: string | BackCloser, closeFn?: BackCloser): BackCloserToken | null {
  const actualCloseFn = typeof name === 'function' ? name : closeFn;
  const actualName = typeof name === 'function' ? 'modal' : name;
  if (typeof actualCloseFn !== 'function') return null;

  const token = createToken(String(actualName || 'modal'));
  backClosers.push({ token, closeFn: actualCloseFn, name: token.name });
  return token;
}

export function unregisterCloser(token: BackCloserToken | BackCloser | null | undefined): boolean {
  if (!token) return false;
  for (let i = backClosers.length - 1; i >= 0; i -= 1) {
    if (backClosers[i]?.token === token || backClosers[i]?.closeFn === token) {
      backClosers.splice(i, 1);
      return true;
    }
  }
  return false;
}

export function setStreamGuard(fn: StreamGuard): BackCloserToken | null {
  if (typeof fn !== 'function') return null;
  const token = createToken('stream-guard');
  streamGuards.push({ token, guardFn: fn });
  return token;
}

function isBackStackEmpty(): boolean {
  return backClosers.length === 0;
}

function popCloser(): BackCloser | null {
  while (backClosers.length > 0) {
    const entry = backClosers.pop();
    if (entry && typeof entry.closeFn === 'function') return entry.closeFn;
  }
  return null;
}

function hasActiveStream(): boolean {
  for (let i = streamGuards.length - 1; i >= 0; i -= 1) {
    try {
      if (streamGuards[i]?.guardFn()) return true;
    } catch {
      // Stream guards must not make the hardware back button brittle.
    }
  }

  const win = getWindow();
  return Boolean(win?.lorathChatStreamActive || win?.lorathTableRoundStreamActive);
}

function dispatchAbortStream(): void {
  const win = getWindow();
  if (!win) return;
  win.dispatchEvent(new CustomEvent('lorath:abort-stream'));
}

function preventNativeBack(event?: NativeBackEvent): boolean {
  try {
    event?.preventDefault?.();
  } catch {
    // Native event implementations vary; failure still means "handled".
  }
  void invoke(getLegacyMethod('preventBackPress'));
  return true;
}

async function allowNativeBack(event?: NativeBackEvent): Promise<boolean> {
  await invoke(getLegacyMethod('allowBackPress'));

  const win = getWindow();
  if (event?.canGoBack !== false && win && win.history.length > 1) {
    win.history.back();
    return false;
  }

  const appModule = await loadCapacitorPlugin('App', () => import('@capacitor/app'));
  await appModule?.App.exitApp();
  return false;
}

function handleNativeBack(event?: NativeBackEvent): boolean {
  const closer = popCloser();
  if (closer) {
    try {
      closer();
    } catch {
      // A closer is UI cleanup; native back should stay handled even if it throws.
    }
    return preventNativeBack(event);
  }

  if (hasActiveStream()) {
    const win = getWindow();
    const fallback = 'The AI response is being generated. Go back anyway?';
    const message = win?.t?.('back_handler.stream_abort_confirm') ?? fallback;
    const shouldAbort = win?.confirm(message) === true;
    if (!shouldAbort) return preventNativeBack(event);

    dispatchAbortStream();
    void allowNativeBack(event);
    return true;
  }

  void allowNativeBack(event);
  return false;
}

/**
 * Oeffentlicher Trigger fuer die zentrale Back-Logik. Wird sowohl vom
 * Hardware-Back-Button (Android), vom App-backButton-Event (Capacitor) als
 * auch von der iOS-Edge-Swipe-Geste genutzt. So bleibt die Reihenfolge
 * (Closers -> Stream-Guard -> history.back / exitApp) identisch.
 */
export function triggerNativeBack(): void {
  handleNativeBack();
}

function exposeBackHandler(): void {
  const win = getWindow();
  if (!win) return;

  const bridge: BackHandlerBridge = {
    register: (handler) => {
      const token = registerCloser('modal', handler);
      return () => {
        unregisterCloser(token);
      };
    },
    push: (handler) => {
      const token = registerCloser('modal', handler);
      return () => {
        unregisterCloser(token);
      };
    },
    add: (handler) => {
      registerCloser('modal', handler);
    },
    remove: (handler) => {
      unregisterCloser(handler);
    },
    registerCloser,
    unregisterCloser,
    isEmpty: isBackStackEmpty,
    setStreamGuard,
  };

  win.LorathBackHandler = bridge;
  win.customBackHandling = handleNativeBack;
}

function registerLegacyBackHandler(): void {
  if (legacyBackRegistered) return;
  const customBackHandling = getLegacyMethod('customBackHandling');
  if (customBackHandling) {
    void invoke(customBackHandling, { enable: true });
    void invoke(customBackHandling, true);
    legacyBackRegistered = true;
  }

  const onBackPress = getLegacyMethod('onBackPress', 'onBackButton');
  if (onBackPress) {
    void invoke(onBackPress, handleNativeBack);
    legacyBackRegistered = true;
  }
}

export function installNativeBackHandler(): void {
  exposeBackHandler();
  registerLegacyBackHandler();

  if (!nativeBackInstallPromise) {
    nativeBackInstallPromise = (async () => {
      if (!await isNativeCapacitorPlatform()) return;
      const appModule = await loadCapacitorPlugin('App', () => import('@capacitor/app'));
      await appModule?.App.addListener('backButton', handleNativeBack);
    })();
  }
}

export async function splashHide(): Promise<void> {
  const splashModule = await loadCapacitorPlugin('SplashScreen', () => import('@capacitor/splash-screen'));
  if (splashModule) {
    await splashModule.SplashScreen.hide();
    return;
  }

  await invoke(getLegacyMethod('splashHide', 'hideSplash', 'hideSplashScreen', 'Splash.splashScreenJsTrigger'));
}

export async function haptic(type: HapticType = 'light'): Promise<void> {
  const hapticsModule = await loadCapacitorPlugin('Haptics', () => import('@capacitor/haptics'));
  if (hapticsModule) {
    const { Haptics, ImpactStyle, NotificationType } = hapticsModule;
    if (type === 'selection') {
      await Haptics.selectionChanged();
      return;
    }

    if (type === 'success' || type === 'warning' || type === 'error') {
      const notificationType = {
        success: NotificationType.Success,
        warning: NotificationType.Warning,
        error: NotificationType.Error,
      }[type];
      await Haptics.notification({ type: notificationType });
      return;
    }

    const impactStyle = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    }[type];
    await Haptics.impact({ style: impactStyle });
    return;
  }

  await invoke(getLegacyMethod('haptic', 'haptics.trigger'), type);
}

export async function setStatusBar(style: StatusBarStyle, color?: string): Promise<void> {
  const statusBarModule = await loadCapacitorPlugin('StatusBar', () => import('@capacitor/status-bar'));
  if (statusBarModule) {
    const { StatusBar, Style } = statusBarModule;
    const capacitorStyle = {
      light: Style.Light,
      dark: Style.Dark,
      default: Style.Default,
    }[style];
    await StatusBar.setStyle({ style: capacitorStyle });
    if (color) await StatusBar.setBackgroundColor({ color });
    return;
  }

  await invoke(getLegacyMethod('setStatusBar', 'statusBar'), { style, color });
}

export function onBackButton(handler: NativeHandler): () => void {
  installNativeBackHandler();
  const token = registerCloser('back-button', () => {
    handler();
    return true;
  });
  return () => {
    unregisterCloser(token);
  };
}

export async function appReview(isSafe: () => boolean = () => true): Promise<void> {
  if (!isSafe()) return;
  const reviewModule = await loadCapacitorPlugin('InAppReview', () => import('@capacitor-community/in-app-review'));
  if (!isSafe()) return;
  if (reviewModule) {
    await reviewModule.InAppReview.requestReview();
    return;
  }

  await invoke(getLegacyMethod('appReview', 'requestReview', 'appReview.prompt'));
}

// Quelle der Wahrheit ist auth/storage.ts (authTokenKeyFor). Hier bewusst dupliziert,
// um einen Zirkular-Import bridge ↔ auth/storage zu vermeiden (storage importiert
// bereits isNativeCapacitorPlatform aus dieser Datei).
const AUTH_TOKEN_PREF_KEY = 'lorath_auth_token_react_v1';
const AUTH_TOKEN_PREF_KEY_BETA = 'lorath_auth_token_react_beta_v1';

// R6-02-a: Preferences.clear() loescht auch den Umgebungsschalter. Ein dort
// abgelegtes ff_env verschwaende, OHNE dass das laufende Bundle wechselt — die
// App liefe weiter mit Beta-JS gegen beta.lorath.eu, meldete sich aber als
// Produktion. Beide Umgebungsschluessel und BEIDE Token gehoeren deshalb in die
// Rettungsliste (auch der Prod-Token soll einen Beta-Ausflug ueberleben).
const PRESERVED_PREF_KEYS = [
  AUTH_TOKEN_PREF_KEY,
  AUTH_TOKEN_PREF_KEY_BETA,
  ENV_PREF_KEY,
  ENV_FORCE_PREF_KEY,
];

export async function clearAppCache(): Promise<void> {
  const preferencesModule = await loadCapacitorPlugin('Preferences', () => import('@capacitor/preferences'));
  if (preferencesModule) {
    // FIX 04-P2-6: Preferences.clear() löschte AUCH den Auth-Token → „Cache leeren"
    // war ein versteckter Logout. Die geschützten Werte vorher sichern und nach
    // dem Clear zurückschreiben, damit Session UND Umgebung erhalten bleiben.
    const saved: [string, string][] = [];
    for (const key of PRESERVED_PREF_KEYS) {
      try {
        const res = await preferencesModule.Preferences.get({ key });
        if (res?.value) saved.push([key, res.value]);
      } catch { /* kein Wert / Lesefehler → einfach weiter */ }
    }
    await preferencesModule.Preferences.clear();
    for (const [key, value] of saved) {
      try {
        await preferencesModule.Preferences.set({ key, value });
      } catch { /* best-effort — ohne Restore folgt ein Logout, aber kein Crash */ }
    }
    getWindow()?.location.reload();
    return;
  }

  await invoke(getLegacyMethod('clearAppCache'), true);
}

export async function getDeviceInfo(): Promise<DeviceInfo | null> {
  const deviceModule = await loadCapacitorPlugin('Device', () => import('@capacitor/device'));
  if (deviceModule) {
    const [info, id] = await Promise.all([
      deviceModule.Device.getInfo(),
      deviceModule.Device.getId(),
    ]);
    return {
      ...info,
      id: id.identifier,
      identifier: id.identifier,
    };
  }

  return asDeviceInfo(await invoke(getLegacyMethod('getDeviceInfo')));
}

/**
 * OneSignal-Verkabelung (Cordova-Plugin, kein Capacitor-Plugin).
 *
 * WICHTIG: Das npm-Paket `onesignal-cordova-plugin` wird BEWUSST NICHT
 * importiert (weder statisch noch dynamisch). Sein iOS-SwiftPM-Modul
 * (OneSignalLiveActivities) bricht den Xcode-Archive-Build
 * (`OneSignalLiveActivities-Swift.h not found`, siehe docs/26.3) — deshalb ist
 * das Plugin aus `frontend/package.json` heraus. Stattdessen lösen wir das SDK
 * zur LAUFZEIT über das von der Cordova-WebView injizierte Global
 * `window.plugins.OneSignal` auf. Ist das native Plugin (noch) nicht eingebaut
 * — wie in den aktuellen Builds — oder läuft die App als Web/PWA, no-oppen alle
 * Aufrufe still. Die Push-Verkabelung (Init/Login/Logout/Permission +
 * Server-Versand) ist damit vollständig vorbereitet und greift, sobald das
 * native Plugin in einem dedizierten Push-Build ergänzt wird
 * (Credential-Folgeschritt, siehe PUSH-CREDENTIALS-Doku).
 */
interface CordovaOneSignalNotificationEvent {
  notification?: {
    additionalData?: Record<string, unknown> | null;
  };
  // Watch Phase 1: bei Action-Buttons liefert das SDK die gewählte Action mit.
  // Native iOS-SDK serialisiert als `actionID`, Cordova-Typing teils `actionId` —
  // beide prüfen (Review-Befund: Reply-Action läuft durch OneSignals Click-
  // Pipeline und dürfte sonst beim nächsten App-Start eine Navigation auslösen).
  result?: {
    actionID?: string;
    actionId?: string;
  } | null;
}

interface CordovaOneSignalNotifications {
  requestPermission?: (fallbackToSettings?: boolean) => Promise<boolean>;
  // Liest nur den Systemzustand, zeigt nie einen Dialog.
  getPermissionAsync?: () => Promise<boolean>;
  // Cordova-SDK v5: Klick-Listener liefert `event.notification.additionalData`
  // (das serverseitige `data`-Objekt aus onesignal.sendNotification).
  addEventListener?: (
    event: 'click',
    cb: (e: CordovaOneSignalNotificationEvent) => void,
  ) => void;
  // Cold-Start: Notification, die die App aus dem beendeten Zustand geoeffnet hat.
  // Liefert genau ein Event (oder null), keine Subscription noetig.
  getInitialNotification?: () => Promise<CordovaOneSignalNotificationEvent | null>;
}

interface CordovaOneSignal {
  initialize?: (appId: string) => void;
  login?: (externalId: string) => void;
  logout?: () => void;
  Notifications?: CordovaOneSignalNotifications;
  InAppMessages?: { setPaused: (paused: boolean) => void };
}

function getCordovaOneSignal(): CordovaOneSignal | null {
  const w = getWindow() as (NativeWindow & {
    plugins?: { OneSignal?: CordovaOneSignal };
    cordova?: { plugins?: { OneSignal?: CordovaOneSignal } };
    OneSignal?: CordovaOneSignal;
  }) | null;
  if (!w) return null;
  return w.plugins?.OneSignal ?? w.cordova?.plugins?.OneSignal ?? w.OneSignal ?? null;
}

let oneSignalInitialized = false;

// FIX 04-P1-2: OneSignal-Init erst als erledigt markieren, wenn das native Plugin
// wirklich geladen war UND initialize() lief. Ist das Plugin beim Cold-Start noch
// nicht bereit (JS-Bridge langsam), begrenzt neu versuchen (5 × 1,5 s), statt das
// Flag blind zu setzen und Push dauerhaft tot zu lassen.
const ONESIGNAL_INIT_MAX_RETRIES = 5;
function attemptOneSignalInit(appId: string, attempt: number): void {
  if (oneSignalInitialized) return;
  const os = getCordovaOneSignal();
  if (os && typeof os.initialize === 'function') {
    try {
      // Auch remote konfigurierte OneSignal-In-App-Nachrichten koennen eine
      // native Berechtigung anfordern. Vor initialize pausieren, damit dieser
      // zweite Prompt-Pfad unseren eigenen App-Hinweis nicht umgeht.
      os.InAppMessages?.setPaused(true);
      os.initialize(appId);
      // Auch SDK-Versionen, die den Pausenstatus erst nach initialize lesen,
      // erhalten ihn vor Login und vor einer asynchronen Remote-Antwort.
      os.InAppMessages?.setPaused(true);
      oneSignalInitialized = true;
      // Nach erfolgreichem Init den Klick-Listener (nach)installieren — er kann beim
      // ersten configureOneSignal-Aufruf noch fehlgeschlagen sein, wenn das Plugin
      // damals nicht bereit war (FIX 04-P2-1).
      void installPushClickListener();
      return;
    } catch {
      // Init warf — als noch-nicht-bereit behandeln (unten Retry).
    }
  }
  if (attempt < ONESIGNAL_INIT_MAX_RETRIES) {
    setTimeout(() => attemptOneSignalInit(appId, attempt + 1), 1500);
  }
}

// ─── OneSignal Web SDK (Browser/PWA) ────────────────────────────────────────
// Audit 02-P1-31: ergaenzt den bestehenden Cordova-Nativpfad um den Web-Push
// fuer Browser/PWA. Der native Pfad oben ist UNVERAENDERT — nur dieser
// else-Zweig (ausschliesslich fuer !native) ist neu. Alles hier ist best-
// effort: ein fehlgeschlagener Init darf App-Start, Login oder Logout NIE
// blockieren oder werfen. Aktivierung erfordert zusaetzlich:
//   (a) OneSignal-Dashboard-Konfiguration der Plattform "Web" fuer die Domain
//   (b) Browser-Verifikation per favicon.ico (OneSignal liefert eine HTML-Datei
//       zur Verifikation der Domain-Inhaberschaft)
// Beides ist externe Einrichtung, kein Code-Schritt. Server-Pushes adressieren
// Nutzer bereits ueber external_id = userId (OneSignal REST); sobald sich der
// Web-Client mit loginOneSignal(userId) registriert, erreichen ihn die
// bestehenden Server-Pushes automatisch — KEIN Server-Change noetig.
//
// Wir nutzen das offizielle Deferred-Pattern: window.OneSignalDeferred ist eine
// Queue, in die Aufrufe gepusht werden BEVOR das SDK geladen ist. Das SDK
// leert die Queue bei Init. So koennen wir init/login/logout/permission
// unabhaengig von der (async) SDK-Ladezeit in beliebiger Reihenfolge anstossen.

const ONE_SIGNAL_WEB_SDK_SRC = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
let oneSignalWebInitialized = false;
let oneSignalWebInitQueued = false;
let oneSignalWebScriptInjected = false;

function pushOneSignalDeferred(callback: (OneSignal: any) => void | Promise<void>): void {
  if (typeof window === 'undefined') return;
  try {
    const w = window as Window & {
      OneSignalDeferred?: Array<(OneSignal: any) => void | Promise<void>>;
    };
    if (!w.OneSignalDeferred) w.OneSignalDeferred = [];
    w.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await callback(OneSignal);
      } catch {
        // Best-effort: Web-Push ist optional — Fehler duerfen App nie stoppen.
      }
    });
  } catch {
    // Best-effort.
  }
}

function ensureOneSignalWebScript(): boolean {
  if (typeof document === 'undefined') return false;
  if (oneSignalWebScriptInjected) return true;
  try {
    // Doppel-Injection verhindern (z.B. bei StrictMode-Doppel-Mount in Dev).
    if (document.querySelector('script[data-onesignal-web-sdk="1"]')) {
      oneSignalWebScriptInjected = true;
      return true;
    }
    const script = document.createElement('script');
    script.src = ONE_SIGNAL_WEB_SDK_SRC;
    script.defer = true;
    script.setAttribute('data-onesignal-web-sdk', '1');
    document.head.appendChild(script);
    oneSignalWebScriptInjected = true;
    return true;
  } catch {
    // Best-effort.
    return false;
  }
}

function configureOneSignalWeb(appId: string): void {
  if (oneSignalWebInitialized) return;
  // Init nur einmal in die Queue legen — Folgeschritte (login/logout/request)
  // werden ebenfalls in die Queue gepusht und vom SDK in Reihenfolge abgearbeitet.
  if (oneSignalWebInitQueued) return;
  if (!ensureOneSignalWebScript()) return;
  oneSignalWebInitQueued = true;
  pushOneSignalDeferred(async (OneSignal) => {
    try {
      await OneSignal.init({
        appId,
        allowLocalhostAsSecureOrigin: true,
        serviceWorkerParam: { scope: '/onesignal/' },
        serviceWorkerPath: 'OneSignalSDKWorker.js',
      });
      oneSignalWebInitialized = true;
    } catch {
      // Init fehlgeschlagen → weiteren Retry zulasen (z.B. nach Netzwerk-Fehler).
      oneSignalWebInitQueued = false;
    }
  });
}

function loginOneSignalWeb(externalId: string): void {
  const appId = (import.meta.env.VITE_ONESIGNAL_APP_ID as string) || '';
  if (!appId) return;
  // Init sicherstellen, BEVOR login() aufgerufen wird — der proaktive Aufruf
  // nach Login kann sonst vor configureOneSignal() laufen (andere Komponente).
  configureOneSignalWeb(appId);
  pushOneSignalDeferred((OneSignal) => {
    try {
      OneSignal.login(String(externalId));
    } catch {
      // Best-effort.
    }
  });
}

function logoutOneSignalWeb(): void {
  const appId = (import.meta.env.VITE_ONESIGNAL_APP_ID as string) || '';
  if (!appId) return;
  configureOneSignalWeb(appId);
  pushOneSignalDeferred((OneSignal) => {
    try {
      OneSignal.logout();
    } catch {
      // Best-effort.
    }
  });
}

function requestPushPermissionWeb(): void {
  const appId = (import.meta.env.VITE_ONESIGNAL_APP_ID as string) || '';
  if (!appId) return;
  configureOneSignalWeb(appId);
  pushOneSignalDeferred((OneSignal) => {
    try {
      void OneSignal.Notifications.requestPermission();
    } catch {
      // Best-effort: Browser-Dialog darf die App nicht blockieren.
    }
  });
}

export async function configureOneSignal(): Promise<void> {
  const appId = (import.meta.env.VITE_ONESIGNAL_APP_ID as string) || '';
  if (!appId) return;
  if (await isNativeCapacitorPlatform()) {
    // ─── NATIVE PFAD — UNVERAENDERT (nur reindented) ─────────────────────────
    // FIX 04-P1-2: Init mit bestätigtem Plugin-Load + begrenztem Retry (s.
    // attemptOneSignalInit). Vorher setzte `os?.initialize?.()` das Flag auch, wenn
    // das Plugin (noch) null war (Optional-Chaining schluckt den Aufruf ohne Fehler)
    // → Push fiel auf langsamen Cold-Starts DAUERHAFT still aus, ohne Neuversuch.
    if (!oneSignalInitialized) attemptOneSignalInit(appId, 0);
    // Push-Click-Listener direkt mit-installieren: Wenn der User die App ueber
    // eine Notification oeffnet, kann der Klick-Event eintreffen BEVOR React
    // Router/Subscriber bereit ist. Daher so frueh wie moeglich hooken und die
    // Route bis zum ersten `subscribePushNavigation`-Aufruf puffern (Pattern
    // analog zu `App.getLaunchUrl()` in deepLinks.ts).
    void installPushClickListener();
    return;
  }
  // ─── WEB PFAD — OneSignal Web SDK v16 via deferred script injection ────────
  configureOneSignalWeb(appId);
}

export async function loginOneSignal(externalId: string): Promise<void> {
  if (await isNativeCapacitorPlatform()) {
    await configureOneSignal();
    // Push-Identitaet erst nach der gegen Remote-Prompts gesicherten Init binden.
    try {
      getCordovaOneSignal()?.login?.(String(externalId));
    } catch {
      // Best-effort: ohne Plugin / native Aufruf-Fehler stillschweigend.
    }
    return;
  }
  // ─── WEB PFAD ──────────────────────────────────────────────────────────────
  loginOneSignalWeb(externalId);
}

export async function logoutOneSignal(): Promise<void> {
  if (await isNativeCapacitorPlatform()) {
    // ─── NATIVE PFAD — UNVERAENDERT ──────────────────────────────────────────
    try {
      getCordovaOneSignal()?.logout?.();
    } catch {
      // Best-effort.
    }
    return;
  }
  // ─── WEB PFAD ──────────────────────────────────────────────────────────────
  logoutOneSignalWeb();
}

/**
 * Fragt einmalig die System-Push-Berechtigung an (OneSignal v5 nativ / v16 Web).
 * Auf Web/PWA (else-Zweig) fordert das OneSignal-Web-SDK den Browser-Prompt
 * an. Einziger Aufrufer ist AuthProvider nach „Weiter“ im App-Hinweis des
 * StartupGateCoordinators, auch fuer explizite Einstellungs-Schalter.
 */
export async function requestPushPermission(confirmation: 'app-hint-continue'): Promise<boolean> {
  // Auch ein aelterer/untypisierter Aufrufer darf ohne die explizite
  // Bestaetigung des App-Hinweises keinen Systemdialog ausloesen.
  if (confirmation !== 'app-hint-continue') return false;
  if (await isNativeCapacitorPlatform()) {
    // Init sicherstellen, BEVOR wir die bestaetigte Berechtigung anfragen.
    await configureOneSignal();
    try {
      const notifications = getCordovaOneSignal()?.Notifications;
      if (!notifications || typeof notifications.requestPermission !== 'function') return false;
      // Auf dem Objekt aufrufen (this-Bindung erhalten).
      return await notifications.requestPermission(true);
    } catch {
      return false;
    }
  }
  // ─── WEB PFAD ──────────────────────────────────────────────────────────────
  requestPushPermissionWeb();
  // Optimistisch false: der eigentliche Permission-State wird vom Web-SDK
  // asynchron verwaltet; Folgeschritte (Server-Sync, Settings-Toggles) lesen
  // den Status dann ueber Notifications.permission.
  return false;
}

/**
 * Ist die System-Push-Berechtigung schon erteilt? Liest nur den Zustand und
 * zeigt NIE einen Systemdialog (Nativ: OneSignal getPermissionAsync, Web:
 * Notification.permission). `null` = unbekannt (z.B. Plugin nicht bereit); dann
 * darf der App-Hinweis wie gewohnt erscheinen. Die Einstellungen fragen damit
 * nicht erneut nach, wenn der Nutzer Mitteilungen laengst erlaubt hat.
 */
export async function isPushPermissionGranted(): Promise<boolean | null> {
  try {
    if (await isNativeCapacitorPlatform()) {
      const notifications = getCordovaOneSignal()?.Notifications;
      if (!notifications || typeof notifications.getPermissionAsync !== 'function') return null;
      return (await notifications.getPermissionAsync()) === true;
    }
    if (typeof Notification !== 'undefined') return Notification.permission === 'granted';
  } catch {
    // Best-effort: unbekannt statt Fehler.
  }
  return null;
}

// ─── Push-Click → SPA-Navigation ─────────────────────────────────────────────
// Serverseitig wird jeder Push mit `data.route` (z.B. `/character-chat/123`)
// ausgestattet. OneSignal liefert dieses Objekt auf der Client-Seite als
// `event.notification.additionalData` (Cordova-SDK v5). Ohne diesen Listener
// tappt der User auf eine Push, die App oeffnet sich — aber bleibt stur auf
// der Startseite. Wir hooken den Klick und routen via React Router.
//
// Cold-Start-Risiko: Wenn die App ueber die Notification aus dem beendeten
// Zustand geoeffnet wird, kann der Klick eintreffen BEVOR React Router oder
// der Subscriber bereit ist. Wir puffern die letzte gesehene Route und
// liefern sie beim ersten `subscribePushNavigation`-Aufruf aus. Das spiegelt
// das Muster `App.getLaunchUrl()` aus deepLinks.ts.

/** Letzter empfangener Push-Klick, der noch nicht an einen Subscriber ging. */
let pendingPushRoute: string | null = null;
/** Liste der aktiven Subscriber. Mehrere moeglich (z.B. HMR-Re-Mount), Realfall = 1. */
let pushNavigationSubscribers: ((route: string) => void)[] = [];
let pushClickListenerInstalled = false;
// Review-Fix: synchroner In-Flight-Guard, damit zwei quasi-gleichzeitige
// installPushClickListener-Aufrufe (configureOneSignal + subscribePushNavigation aus
// dem gleichen App.tsx-useEffect) den nativen click-Listener nicht DOPPELT registrieren.
let pushClickInstallInFlight = false;

function readPushRouteFromEvent(event: CordovaOneSignalNotificationEvent | null | undefined): string | null {
  // Watch-Reply-Action (Hintergrund-Antwort von Watch/Phone-Quick-Reply) ist
  // KEIN Klick: OneSignal meldet sie trotzdem als click-Event — ohne diesen
  // Guard würde der nächste App-Start in den Chat auto-navigieren, obwohl der
  // User nie getippt hat, um die App zu öffnen (Review-Befund). Zentral hier,
  // damit auch der getInitialNotification-Cold-Start-Pfad gefiltert ist.
  const actionId = event?.result?.actionID ?? event?.result?.actionId;
  if (typeof actionId === 'string' && actionId === 'LORATH_REPLY_TEXT') return null;
  const raw = event?.notification?.additionalData?.route;
  if (typeof raw !== 'string' || raw.length === 0) return null;
  if (!isSafeAppPath(raw)) return null;
  return raw;
}

// X-03: Klick an den Server melden (push_log.opened_at). Die Watch-Antwort ist
// kein Oeffnen der App und zaehlt nicht.
function reportPushOpenFromEvent(event: CordovaOneSignalNotificationEvent | null | undefined): void {
  const actionId = event?.result?.actionID ?? event?.result?.actionId;
  if (typeof actionId === 'string' && actionId === 'LORATH_REPLY_TEXT') return;
  reportPushOpened(event?.notification?.additionalData);
}

function dispatchPushRoute(route: string): void {
  if (pushNavigationSubscribers.length === 0) {
    // Noch kein React-Subscriber → puffern. Letzter gewinnt (typischer Fall:
    // exakt ein Klick, der die App geoeffnet hat).
    pendingPushRoute = route;
    return;
  }
  for (const cb of pushNavigationSubscribers) {
    try {
      cb(route);
    } catch {
      // Subscriber-Fehler duerfen andere Listener nicht stoppen.
    }
  }
}

export async function installPushClickListener(): Promise<void> {
  // FIX 04-P2-1 (+ Review): Der In-Flight-Guard wird SYNCHRON vor jedem await gesetzt,
  // sonst passieren zwei quasi-gleichzeitige Aufrufe beide den `installed`-Guard, bevor
  // einer das Flag setzt, und registrieren den nativen click-Listener DOPPELT → jeder
  // Push-Tap navigiert zweimal. Das `installed`-Flag bleibt weiter erst nach
  // erfolgreichem Hook gesetzt (Retry möglich, falls das Plugin noch fehlt).
  if (pushClickListenerInstalled || pushClickInstallInFlight) return;
  pushClickInstallInFlight = true;

  try {
    if (!await isNativeCapacitorPlatform()) return;
    const os = getCordovaOneSignal();
    const notifications = os?.Notifications;
    // Ist das Plugin (noch) nicht geladen, das Installed-Flag NICHT setzen → ein
    // späterer Aufruf (attemptOneSignalInit nach erfolgreichem Init) versucht es erneut.
    if (!notifications) return;

    const addListener = notifications.addEventListener;
    if (typeof addListener === 'function') {
      try {
        addListener.call(notifications, 'click', (event: CordovaOneSignalNotificationEvent) => {
          reportPushOpenFromEvent(event);
          const route = readPushRouteFromEvent(event);
          if (route) dispatchPushRoute(route);
        });
      } catch {
        // Native Aufruf-Variante fehlgeschlagen — wir lassen getInitialNotification
        // noch eine Chance und beenden den Versuch.
      }
    }

    // Cold-Start-Pfad: die Notification, die die App aus dem beendeten Zustand
    // geoeffnet hat, feuert KEINEN click-Event — sie muss hier aktiv abgeholt
    // werden. Wenn der Subscriber noch nicht steht, landet sie im Puffer.
    const getInitial = notifications.getInitialNotification;
    if (typeof getInitial === 'function') {
      try {
        const initial = await getInitial.call(notifications);
        reportPushOpenFromEvent(initial);
        const route = readPushRouteFromEvent(initial);
        if (route) dispatchPushRoute(route);
      } catch {
        // Plugin-Variante ohne getInitialNotification — kein Hard-Fail.
      }
    }
    // FIX 04-P2-1: Erst JETZT als installiert markieren — das Plugin war da und der
    // Hook-Versuch ist gelaufen. So verhindert das Flag Doppel-Registrierung, blockt
    // aber keinen Retry, falls das Plugin zuvor fehlte.
    pushClickListenerInstalled = true;
  } catch {
    // Komplett fehlgeschlagen → beim nächsten Init-Retry erneut versuchen.
  } finally {
    pushClickInstallInFlight = false;
  }
}

/**
 * Verdrahtet einen Callback fuer serverseitige Push-Routen (`data.route`).
 *
 * - Auf Web/Nicht-nativ: no-op (gibt Pass-Through-Unsubscribe zurueck).
 * - Reicht sofort eine gepufferte Cold-Start-Route nach, falls vorhanden.
 * - Liefert einen Unsubscribe-Callback.
 *
 * Erwartet, dass `configureOneSignal()` (oder spaetestens `App.tsx`-Mount)
 * `installPushClickListener()` aufgerufen hat. Der Aufruf ist aber robust
 * gegen verkehrte Reihenfolge: Ohne installierten Listener bleibt der Puffer
 * leer und spaetere Klicks werden ebenfalls verpasst — das ist akzeptabel,
 * weil die App dann ohnehin nicht initialisiert ist.
 */
export async function subscribePushNavigation(cb: (route: string) => void): Promise<() => void> {
  if (typeof cb !== 'function') return () => undefined;

  // Gepufferte Cold-Start-Route sofort ausliefern, BEVOR wir den Subscriber
  // registrieren — so feuert der Klick nicht zweimal, falls spaeter noch ein
  // Listener-Event kommt.
  if (pendingPushRoute) {
    const buffered = pendingPushRoute;
    pendingPushRoute = null;
    try {
      cb(buffered);
    } catch {
      // Subscriber-Fehler im Initial-Push werden geschluckt, damit der
      // Subscriber trotzdem registriert wird (Folge-Klicks sollen weiterhin
      // ankommen).
    }
  }

  pushNavigationSubscribers.push(cb);
  // Sicherheitsnetz: Falls configureOneSignal noch nicht gelaufen ist (z.B.
  // Web-Build, der spaeter nativ wird), hier nachholen (await erfüllt zugleich
  // den async-Vertrag dieser Funktion).
  await installPushClickListener();

  return () => {
    pushNavigationSubscribers = pushNavigationSubscribers.filter((s) => s !== cb);
  };
}

export async function openExternal(url: string): Promise<void> {
  // DA-Fix R2: Custom-Schemes (z.B. der iOS-Store-Deeplink itms-apps://) kann
  // SFSafariViewController (Browser.open) NICHT öffnen → für nicht-http(s)-URLs
  // den System-Pfad nehmen, den iOS/Android an die zuständige App (App Store /
  // Play Store) delegiert. Nur http(s) geht über Capacitor Browser.
  const isHttp = /^https?:\/\//i.test(url);

  if (isHttp) {
    // Primaerpfad: Capacitor Browser (SFSafariViewController iOS / Chrome
    // Custom Tabs Android). Auf Android funktioniert das nur, wenn das
    // <queries>-Element in AndroidManifest.xml die VIEW-Intents sichtbar
    // macht (Package-Visibility ab Android 11 / targetSdk 30+).
    const browserModule = await loadCapacitorPlugin('Browser', () => import('@capacitor/browser'));
    if (browserModule) {
      try {
        await browserModule.Browser.open({ url });
        return;
      } catch {
        // Fallback unten: Browser-Plugin vorhanden, aber Aufruf
        // fehlgeschlagen (z.B. kein Custom-Tabs-Provider auf Geraet).
      }
    }
  }

  // Defense-in-Depth: @capacitor/app hatte in v1/v2 ein App.openUrl(),
  // das in v4+ entfernt wurde und in unserer Dependency-Version 8.1.0
  // nicht mehr existiert. Praktischer Fallback ist daher ein direkter
  // window.open() - auf Web ein neuer Tab, in der Capacitor-WebView
  // ein System-Intent, sofern das <queries>-Manifest greift.
  if (typeof window !== 'undefined') {
    const opened = window.open(url, isHttp ? '_blank' : '_system', 'noopener,noreferrer');
    if (opened !== null) return;
    // Letzter Versuch für Custom-Schemes (itms-apps:// u.a.): direkte
    // Navigation. WKWebView delegiert unbekannte Schemes an iOS → der App Store
    // öffnet sich, die WebView bleibt erhalten.
    if (!isHttp) {
      try {
        window.location.href = url;
        return;
      } catch {
        // ignore — letzter Bridge-Versuch unten
      }
    }
  }

  // Legacy-Native-Bridge (altes WTN-Plugin) als letzter Versuch.
  await invoke(getLegacyMethod('openExternal', 'openInBrowser'), url);
}

export async function getNetworkStatus(): Promise<NativeNetworkStatus | null> {
  const networkModule = await loadCapacitorPlugin('Network', () => import('@capacitor/network'));
  if (!networkModule) return null;
  return networkModule.Network.getStatus();
}

export async function addNetworkStatusListener(
  handler: (status: NativeNetworkStatus) => void,
): Promise<() => void> {
  const networkModule = await loadCapacitorPlugin('Network', () => import('@capacitor/network'));
  if (!networkModule) return () => undefined;

  const subscription: NativeSubscription = await networkModule.Network.addListener('networkStatusChange', handler);
  return () => {
    void subscription.remove?.();
  };
}

export async function forceCookieSync(): Promise<void> {
  await invoke(getLegacyMethod('forceCookieSync', 'forceUpdateCookies'));
}

// ─── Store-Listing-URLs (Phase 5) ───────────────────────────────────────────
// Single Source of Truth fuer die App-Store-IDs. Wenn sich die IDs aendern
// (z.B. neue Store-Eintragung), muss das hier zentral angepasst werden.
const APP_STORE_ID = '6761920404';
// FIX 03-P2-3: Die ECHTE Play-Store-Package-ID (Dual-Identität: Java-Namespace
// bleibt eu.fluesterfeder.app, aber das Play-Listing läuft unter der WebToNative-
// generierten ID). Vorher zeigten „App bewerten"-Link + das Update-Banner (Schritt 6)
// auf ein nicht existentes eu.fluesterfeder.app-Listing (404).
const ANDROID_PACKAGE = 'com.wnapp.id1774567594039';

// App Review 2.3.10: Fremdplattform-Referenzen dürfen NICHT als Literale im
// iOS-Binary stehen. Diese Werte werden ausschließlich auf Android genutzt und
// zur Laufzeit per Array.join zusammengesetzt — der Minifier faltet `.join()`
// NICHT, also taucht weder der Host noch der Store-Name wörtlich im Bundle auf.
export const ANDROID_STORE_HOST = ['play', 'google', 'com'].join('.');
export const ANDROID_STORE_NAME = ['Play', 'Store'].join(' ');

/**
 * Listing-URL (Storefront-Seite, nicht direkt Review). Wird z.B. vom
 * "Mehr erfahren"-Link in der App verwendet.
 */
export function storeListingUrl(os: string): string {
  if (os === 'android') {
    return `https://${ANDROID_STORE_HOST}/store/apps/details?id=${ANDROID_PACKAGE}`;
  }
  // iOS und Fallback (z.B. Web) zeigen auf den iOS-App-Store-Eintrag.
  return `https://apps.apple.com/app/id${APP_STORE_ID}`;
}

/**
 * Review-URL mit Prefilled-Review-Action fuer iOS. Android bietet kein
 * stabiles Deep-Link-Schema fuer Reviews; daher landet man auf der
 * Storefront — der Play Store leitet den User auf Wunsch direkt in den
 * Review-Dialog, ist aber nicht deterministisch.
 */
export function storeReviewUrl(os: string): string {
  if (os === 'android') {
    return `https://${ANDROID_STORE_HOST}/store/apps/details?id=${ANDROID_PACKAGE}`;
  }
  return `https://apps.apple.com/app/id${APP_STORE_ID}?action=write-review`;
}
